import React, { useEffect, useState } from 'react';
import { DropResult, DragDropContext, Droppable } from 'react-beautiful-dnd';
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';

import { reorder } from '../../../../common/reorder.helper';
import ConfirmDialog from '../../../../common/dialogs/ConfirmDialog';
import TestStepItem from './TestStepItem';
import { TestStep } from './TestStep.interface';
import { useDeleteTestStep } from './TestStep.hooks';

export function updateStepNumber(
  testSteps: TestStep[],
  startIndex: number = 0
): TestStep[] {
  startIndex = startIndex > -1 ? startIndex : 0;
  for (let index: number = startIndex; index < testSteps.length; index++) {
    testSteps[index].stepNo = index + 1;
  }
  return testSteps;
}

export interface TestStepEditorProps {
  testSteps: TestStep[];
  isLoading?: boolean;
  verificationId?: number;
  validationId?: number;
  disabled: boolean;
  onChange?: (newTestSteps: TestStep[], noSave?: boolean) => void;
}

const TestStepEditor: React.FC<TestStepEditorProps> = (
  props: TestStepEditorProps
) => {
  const {
    testSteps,
    isLoading = false,
    verificationId,
    validationId,
    disabled,
    onChange
  } = props;
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [draggingHeight, setDraggingHeight] = useState<number | undefined>();
  const [testStepToRemove, setTestStepToRemove] = useState<
    TestStep | undefined
  >();
  const [, deleteTestStep] = useDeleteTestStep(testStepToRemove?.id);

  const onChangeValue = (
    testStep: TestStep,
    updateTestStep: Partial<TestStep>
  ) => {
    const index: number = testSteps.indexOf(testStep);
    if (index > -1) {
      const newTestSteps: TestStep[] = [...testSteps];
      newTestSteps[index] = { ...testSteps[index], ...updateTestStep };
      onChange?.(newTestSteps);
    }
  };

  const addStep = (testStep: TestStep, index?: number) => {
    testStep = { ...testStep };
    if (verificationId) {
      testStep.verificationId = verificationId;
    }
    if (validationId) {
      testStep.validationId = validationId;
    }

    index = typeof index === 'number' ? index : testSteps.length;
    const newTestSteps: TestStep[] = [...testSteps];
    newTestSteps.splice(index, 0, testStep);
    onChange?.(updateStepNumber(newTestSteps));
  };

  const removeStep = (testStep: TestStep) => {
    const index: number = testSteps.indexOf(testStep);
    if (index > -1) {
      const newTestSteps: TestStep[] = [...testSteps];
      newTestSteps.splice(index, 1);
      onChange?.(updateStepNumber(newTestSteps));
    }
  };

  const moveStep = (sourceIndex: number, destinationIndex: number) => {
    const newTestSteps: TestStep[] = reorder(
      testSteps,
      sourceIndex,
      destinationIndex
    );
    onChange?.(updateStepNumber(newTestSteps));
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }
    moveStep(source.index, destination.index);
  };

  useEffect(() => {
    // if no testSteps, add 1
    if (testSteps.length < 1) {
      addStep({ stepNo: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testSteps]);

  return (
    <TableContainer
      style={
        typeof draggingHeight === 'number'
          ? { paddingBottom: draggingHeight }
          : undefined
      }
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: 1 }} />
            <TableCell style={{ width: 1 }}>Step #</TableCell>
            <TableCell>Step Description</TableCell>
            <TableCell>Sample Data</TableCell>
            <TableCell>Expected Result</TableCell>
            <TableCell style={{ width: 1 }} />
          </TableRow>
        </TableHead>
        {/* fix duplicate value issue by re-rendering on change length */}
        <DragDropContext
          onDragEnd={handleDragEnd}
          key={`steps-${testSteps.length}`}
        >
          <Droppable droppableId='steps'>
            {(provided, _snapshot) => (
              <TableBody ref={provided.innerRef}>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} padding='none'>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {testSteps.map((testStep: TestStep, index: number) => (
                  <TestStepItem
                    key={`${testStep.id}-${testStep.stepNo}`}
                    onAddStep={addStep}
                    {...{ testStep, index, setDraggingHeight }}
                    onChange={(updateTestStep: Partial<TestStep>) => {
                      onChangeValue(testStep, updateTestStep);
                    }}
                    disabled={disabled}
                    onRemoveStep={() => {
                      setTestStepToRemove(testStep);
                      setOpenDialog(true);
                    }}
                  />
                ))}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
      </Table>
      <ConfirmDialog
        open={openDialog && !!testStepToRemove}
        title={
          'Remove Test Step' +
          (testStepToRemove ? ` (Step ${testStepToRemove.stepNo})` : '')
        }
        confirmLabel='Continue'
        message='Are you sure you want to remove this step?'
        onCancel={() => setOpenDialog(false)}
        onConfirm={() => {
          if (testStepToRemove) {
            // if only one step, clear instead of removing
            if (testSteps.length === 1) {
              const updatedTestStep: TestStep = {
                ...testStepToRemove,
                description: null,
                sampleData: null,
                expectedResult: null
              };
              onChangeValue(testStepToRemove, updatedTestStep);
            } else {
              deleteTestStep();
              removeStep(testStepToRemove);
            }
          }
          setOpenDialog(false);
          setTestStepToRemove(undefined);
        }}
      />
    </TableContainer>
  );
};

export default TestStepEditor;
